import { FormHelperText, Grid, TermsOfServiceCheckbox } from '@medely/web-components';
import { useFirebaseContext, Button } from '@medely/ui-kit';
import { PhoneField, TextField, Field } from '@medely/web-components/forms';
import { axios } from 'utils/networkRequests';
import { composeValidation, email, phone, required } from 'core/utils/validation';
import useSnackbar from 'hooks/useSnackbar';
import * as R from 'ramda';
import React from 'react';
import { Form } from 'react-final-form';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { decodeInviteToken } from 'utils/token';
import { minLength } from 'utils/validation';
import config from 'config';
import { isDevelopment } from 'utils/env';

type AcceptInviteFormValues = {
  account: {
    token?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
    password?: string;
  };
  terms_of_service?: boolean;
};

const phoneValidator = composeValidation(required, phone);
const emailValidator = composeValidation(required, email);
const passwordValidator = composeValidation(required, minLength(8));

const AcceptInviteForm = () => {
  const { login } = useFirebaseContext();
  const snackbar = useSnackbar();
  const [{ token }] = useQueryParams({
    token: withDefault(StringParam, ''),
  });
  const { email, new_account_first_name, new_account_last_name, new_account_entity_type } =
    decodeInviteToken(token);

  const isImpersonating = window.localStorage.getItem('impersonating') === 'true';
  const testMode = isDevelopment() || isImpersonating;

  const handleSubmit = async (values: AcceptInviteFormValues) => {
    try {
      await axios.post(`/v3/professional/accounts/accept_invite`, {
        account: R.omit(['email'], values.account),
      });
      login({ email, password: values.account.password });
    } catch (e) {
      snackbar.error(e);
    }
  };

  return (
    <Form
      initialValues={{
        account: {
          email,
          token,
          first_name: new_account_first_name,
          last_name: new_account_last_name,
        },
      }}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3.5}>
            <Grid item xs={6}>
              <TextField name="account[first_name]" label="First Name" validate={required} />
            </Grid>
            <Grid item xs={6}>
              <TextField name="account[last_name]" label="Last Name" validate={required} />
            </Grid>
            <Grid item xs={12}>
              <TextField name="account[email]" label="Email" disabled validate={emailValidator} />
            </Grid>
            <Grid item xs={12}>
              <PhoneField
                name="account[phone_number]"
                label="Phone Number"
                validate={phoneValidator}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="account[password]"
                type="password"
                label="Password"
                validate={passwordValidator}
              />
            </Grid>
            {new_account_entity_type === 'agency' && (
              <Grid item xs={12}>
                <Field name="terms_of_service" validate={required}>
                  {({ input, meta }) => (
                    <>
                      <TermsOfServiceCheckbox
                        accessId={config.ironclad.accessId}
                        groupKey={config.ironclad.groupKeys.checkbox.tosAgency}
                        signerId={email}
                        testMode={testMode}
                        onInvalid={() => {
                          input.onChange(undefined);
                        }}
                        onValid={() => {
                          input.onChange('valid');
                        }}
                      />
                      {meta.touched && meta.error && (
                        <FormHelperText error>{meta.error}</FormHelperText>
                      )}
                    </>
                  )}
                </Field>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                fullWidth
                disabled={submitting}
              >
                Sign Up
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
};

export default AcceptInviteForm;
